import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { EActionType } from 'components/activity/enums';
import { notificationsApi } from 'components/notifications/notificationsApi';
import { getLocalMerchantId } from 'components/settings/localStore';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { userApi } from 'components/user/userApi';
import logger from 'lib/logger';
import { useAuth } from 'lib/useAuth';
import { setReturnUrl } from 'store/auth/authSlice';
import { setGuestContactInformation } from 'store/guest/guestSlice';
import { ApplicationState } from 'store/store';

interface UseSummaryGuestConversionArgs {
    orderId: number;
    /** Called after the inline login or signup+activate flow completes — typically used to
     *  refetch the order so the just-merged ownership becomes visible on the summary page. */
    onAuthComplete?: () => void;
}

/** Keep enough state in localStorage to survive a page refresh — specifically so a user
 *  who has already submitted their email doesn't get prompted again on refresh (which would
 *  trigger a duplicate receipt send). localStorage rather than sessionStorage because
 *  `clearSessionStorage` (lib/helpers) is called inside the page's `fetchOrder` effect on
 *  every order summary load — it would wipe per-tab state before the next refresh could
 *  read it. Per-orderId keying avoids cross-order bleed; the entry is cleared once the
 *  auth flow completes (`authStage === 'done'`).
 *
 *  Note: we deliberately do NOT persist `loading` (a transient UI state) or any password the
 *  user types into the inline LoginForm/SignUpForm (Formik fields aren't persisted).
 */
const LOCAL_STORAGE_KEY_PREFIX = 'GUEST_CONVERSION_STATE';

interface PersistedGuestConversionState {
    sent: boolean;
    accountExists: boolean;
    submittedEmail: string | undefined;
    authStage: InlineAuthStage;
}

const INLINE_AUTH_STAGES: readonly InlineAuthStage[] = [
    'idle',
    'login',
    'signup',
    'activate',
    'forgot-password',
    'two-factor',
    'done'
];

function isInlineAuthStage(value: unknown): value is InlineAuthStage {
    return typeof value === 'string' && (INLINE_AUTH_STAGES as readonly string[]).includes(value);
}

function isPersistedGuestConversionState(value: unknown): value is PersistedGuestConversionState {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    return (
        typeof candidate.sent === 'boolean' &&
        typeof candidate.accountExists === 'boolean' &&
        (candidate.submittedEmail === undefined || typeof candidate.submittedEmail === 'string') &&
        isInlineAuthStage(candidate.authStage)
    );
}

function readPersistedState(orderId: number): PersistedGuestConversionState | null {
    if (typeof localStorage === 'undefined') {
        return null;
    }
    try {
        const raw = localStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}-${orderId}`);
        if (!raw) {
            return null;
        }
        const parsed: unknown = JSON.parse(raw);
        return isPersistedGuestConversionState(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writePersistedState(orderId: number, state: PersistedGuestConversionState): void {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}-${orderId}`, JSON.stringify(state));
    } catch {
        // Quota or disabled storage — best-effort persistence.
    }
}

function clearPersistedState(orderId: number): void {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        localStorage.removeItem(`${LOCAL_STORAGE_KEY_PREFIX}-${orderId}`);
    } catch {
        // ignore
    }
}

/**
 * Inline auth flow stages once the user has chosen to proceed past the post-submit prompt.
 *  - `idle`: the prompt is showing but the user hasn't engaged.
 *  - `login`: inline LoginForm rendered (taken when `accountExists === true`).
 *  - `signup`: inline SignUpForm rendered (taken when `accountExists === false`).
 *  - `activate`: signup completed; inline ActivateForm rendered to collect the verification code.
 *  - `forgot-password`: inline ForgotPasswordForm rendered; entered from the login stage.
 *  - `done`: auth flow completed end-to-end; receipt component can close the inline area.
 */
export type InlineAuthStage =
    | 'idle'
    | 'login'
    | 'signup'
    | 'activate'
    | 'forgot-password'
    | 'two-factor'
    | 'done';

export interface UseSummaryGuestConversionResult {
    loading: boolean;
    sent: boolean;
    /** True iff the submitted email already belongs to another account. Only meaningful for guests. */
    accountExists: boolean;
    /** The email the user successfully submitted — used by the inline post-submit prompts. */
    submittedEmail: string | undefined;
    /** The identity (email or phone) that matched an existing account — used to pre-fill the
     *  inline login form. Distinct from submittedEmail because on email+phone merchants the
     *  account may have been found via the phone probe, not the submitted email. */
    loginIdentity: string | undefined;
    submit: (email: string) => Promise<void>;
    /**
     * Send a receipt to the given email without touching the auth flow. Useful for phone-auth
     * merchants where the conversion identity is the phone (not the email), so the email
     * input is a pure side-channel for receipt delivery — no credentials check, no stage change.
     * Throws on transport errors; the caller is responsible for any UI loading/sent state.
     */
    sendReceiptOnly: (email: string) => Promise<void>;
    /** Current stage of the inline auth flow — see {@link InlineAuthStage}. */
    authStage: InlineAuthStage;
    /** Move the inline auth flow to a specific stage. Used by the consumer to swap between
     *  login and signup when the user clicks the "Don't have an account?" / "Already have
     *  an account?" links inside the inline form. */
    setAuthStage: (stage: InlineAuthStage) => void;
    /** Start the inline auth flow. Picks `login` or `signup` based on `accountExists`. */
    openInlineAuth: () => void;
    /** Close the inline form (e.g. user wants to back out before completing). */
    closeInlineAuth: () => void;
    /** Called by the inline SignUpForm's onSuccess — transitions to activate stage. */
    handleSignupSuccess: () => void;
    /** Called by inline LoginForm or ActivateForm's onSuccess — marks the flow complete. */
    handleAuthComplete: () => void;
    /** Switch to the inline ForgotPasswordForm from the login stage. */
    handleForgotPassword: () => void;
    /** Switch to the inline TwoFactorForm; called with the identity the user submitted. */
    handleTwoFactor: (identity: string) => void;
    /** The identity passed to handleTwoFactor — used to pre-fill the inline TwoFactorForm. */
    twoFactorIdentity: string | undefined;
    /**
     * Persist the current URL into Redux + localStorage. Kept exported so callers can stash
     * the URL before any navigation (e.g. for forgot-password / SSO redirects within inline forms).
     */
    rememberCurrentUrl: () => void;
    /** True when the merchant uses phone-only authentication. Receipt components branch on this
     *  to skip the email-entry gateway in favour of the inline auth form. */
    isPhoneAuth: boolean;
}

/**
 * Drives the unified guest email-receipt flow:
 *   1. Send the receipt to the entered email (always).
 *   2. For guests, attempt to attach the email to their guest user record.
 *      - On success → email is free → render an inline "Create account?" prompt.
 *      - On `E-PLA-1021` → email belongs to another account → render an inline "Sign in?" prompt.
 * For non-guests, only step 1 runs; the prompts are guest-only.
 */
export function useSummaryGuestConversion({
    orderId,
    onAuthComplete
}: UseSummaryGuestConversionArgs): UseSummaryGuestConversionResult {
    const { user, isGuest } = useAuth();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { pathname, search } = useLocation();
    const { settings } = useSelector((state: ApplicationState) => state.settings);

    // Phone-auth merchants have no useful email-based receipt+auth coupling — email is just
    // an optional side-channel for receipt delivery, and the conversion identity is the phone.
    const isPhoneAuth = getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled) === 'phone';

    // Restore the prior receipt-state from localStorage on mount so a page refresh doesn't
    // ask the user to re-enter their email (which would trigger a duplicate receipt send).
    // Read once per orderId via a lazy initializer.
    const initial = React.useMemo(() => readPersistedState(orderId), [orderId]);

    const [loading, setLoading] = React.useState(false);
    const [sent, setSent] = React.useState(initial?.sent ?? false);
    const [accountExists, setAccountExists] = React.useState(initial?.accountExists ?? false);
    const [submittedEmail, setSubmittedEmail] = React.useState<string | undefined>(initial?.submittedEmail);
    const [loginIdentity, setLoginIdentity] = React.useState<string | undefined>(undefined);
    // For phone-auth merchants the inline signup form is the primary surface — no email
    // gateway. Default to `signup` so the form renders immediately (the user can still flip
    // to login via the "Already have an account?" link).
    const [authStage, setAuthStage] = React.useState<InlineAuthStage>(
        (isGuest ? initial?.authStage : undefined) ?? (isPhoneAuth && isGuest ? 'signup' : 'idle')
    );

    // Persist whenever the meaningful state changes; clear when the auth flow completes so
    // a second order doesn't get the previous order's flow restored on refresh.
    React.useEffect(() => {
        if (authStage === 'done') {
            clearPersistedState(orderId);
            return;
        }
        if (sent || authStage !== 'idle') {
            writePersistedState(orderId, { sent, accountExists, submittedEmail, authStage });
        }
    }, [accountExists, authStage, orderId, sent, submittedEmail]);

    // Phone-auth merchants don't have an email gateway — the inline auth form is rendered
    // immediately. To get the right starting form (login vs signup), probe the phone against
    // existing accounts on mount. If the phone matches, switch to `login`; otherwise stay on
    // `signup` (the default for phone-auth). Only runs once and skips if we restored a
    // persisted authStage (the user has already engaged with the flow).
    const phoneCheckDoneRef = React.useRef<boolean>(false);
    React.useEffect(() => {
        if (phoneCheckDoneRef.current) {
            return;
        }
        if (!isPhoneAuth || !isGuest || !user?.id) {
            return;
        }
        if (initial?.authStage) {
            // Restored from persistence — don't overwrite the user's progress.
            phoneCheckDoneRef.current = true;
            return;
        }
        const phone = user.phoneNumberLastUsedToOrder;
        if (!phone) {
            return;
        }
        phoneCheckDoneRef.current = true;
        (async () => {
            try {
                const [success] = await userApi.updateGuestCredentials(user.id, { identity: phone });
                if (!success) {
                    // Phone matches an existing account → flip to login.
                    setAccountExists(true);
                    setAuthStage('login');
                }
                // If the phone was free, it's now attached to the guest user — leave the
                // signup form rendered (the default for phone-auth).
            } catch (e) {
                logger.error(e);
            }
        })();
    }, [initial?.authStage, isGuest, isPhoneAuth, user?.id, user?.phoneNumberLastUsedToOrder]);

    // Persist the current URL so signup.tsx's post-success effect can return the user here.
    // signup.tsx:315 reads Redux `returnUrl` first, then falls back to `getOnCloseRedirectUrl`.
    // Without this, the user lands wherever an earlier flow last set the URL.
    const rememberCurrentUrl = React.useCallback(() => {
        const fullPath = pathname + search;
        dispatch(setReturnUrl(fullPath));
        const merchantId = getLocalMerchantId();
        if (merchantId) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
    }, [dispatch, pathname, search]);

    const submit = React.useCallback(
        async (email: string) => {
            if (!user?.id) {
                return;
            }
            setLoading(true);
            try {
                const list = await userApi.getActivityHistory(user.id);
                const actionId = list.items.find(
                    action =>
                        (action.type === EActionType.ORDER_USER_BILLED ||
                            action.type === EActionType.ORDER_COMPLETED) &&
                        action.metadata.order &&
                        action.metadata.order.id === orderId
                )?._id;

                if (!actionId) {
                    enqueueSnackbar(t('GENERAL_ERROR_PROCESSING_REQUEST'), { variant: 'error' });
                    return;
                }

                // Send the receipt first — the user gets value regardless of which branch
                // the credentials check ends up taking.
                await notificationsApi.resendReceipt({ userEmail: email, actionId });

                let accountExistsByEither = false;
                let matchedIdentity: string | undefined;
                if (isGuest) {
                    try {
                        const [success] = await userApi.updateGuestCredentials(user.id, {
                            identity: email
                        });
                        if (success) {
                            // Email is free and now attached to the guest. Cache locally so the
                            // signup form pre-fills cleanly when they tap "Create account".
                            dispatch(setGuestContactInformation({ identity: email }));
                        } else {
                            // E-PLA-1021 — email belongs to another account.
                            accountExistsByEither = true;
                            matchedIdentity = email;
                        }
                    } catch (e) {
                        // Don't fail the whole flow on a credentials hiccup — the receipt was sent.
                        logger.error(e);
                    }

                    // If the email wasn't recognised and we have a phone on file (from the
                    // basket capture flow), also probe the phone — it's often a more reliable
                    // identity for returning customers. If the phone matches an existing
                    // account, route to login as if the email had matched.
                    const phone = user?.phoneNumberLastUsedToOrder;
                    if (!accountExistsByEither && phone) {
                        try {
                            const [phoneSuccess] = await userApi.updateGuestCredentials(user.id, {
                                identity: phone
                            });
                            if (!phoneSuccess) {
                                accountExistsByEither = true;
                                matchedIdentity = phone;
                            }
                        } catch (e) {
                            logger.error(e);
                        }
                    }
                }

                setSubmittedEmail(email);
                setLoginIdentity(matchedIdentity);
                setAccountExists(accountExistsByEither);
                setSent(true);

                // For guests, drop straight into the appropriate inline auth form — login
                // when either identity is recognised, signup otherwise. The receipt was
                // already sent, so the user can still ignore the form if they don't want to convert.
                if (isGuest) {
                    rememberCurrentUrl();
                    setAuthStage(accountExistsByEither ? 'login' : 'signup');
                }
            } catch (e) {
                logger.error(e);
                enqueueSnackbar(t('GENERAL_ERROR_PROCESSING_REQUEST'), { variant: 'error' });
            } finally {
                setLoading(false);
            }
        },
        [
            dispatch,
            enqueueSnackbar,
            isGuest,
            orderId,
            rememberCurrentUrl,
            t,
            user?.id,
            user?.phoneNumberLastUsedToOrder
        ]
    );

    const sendReceiptOnly = React.useCallback(
        async (email: string) => {
            if (!user?.id) {
                return;
            }
            const list = await userApi.getActivityHistory(user.id);
            const actionId = list.items.find(
                action =>
                    (action.type === EActionType.ORDER_USER_BILLED ||
                        action.type === EActionType.ORDER_COMPLETED) &&
                    action.metadata.order &&
                    action.metadata.order.id === orderId
            )?._id;
            if (!actionId) {
                throw new Error('No action found for order');
            }
            await notificationsApi.resendReceipt({ userEmail: email, actionId });
        },
        [orderId, user?.id]
    );

    const openInlineAuth = React.useCallback(() => {
        rememberCurrentUrl();
        setAuthStage(accountExists ? 'login' : 'signup');
    }, [accountExists, rememberCurrentUrl]);

    const closeInlineAuth = React.useCallback(() => {
        setAuthStage('idle');
    }, []);

    const handleSignupSuccess = React.useCallback(() => {
        // Register always requires activation — even the `type: 'success'` branch needs the
        // user to enter the verification code emitted by the backend. Transition into the
        // inline activate stage so ActivateForm replaces SignUpForm in the same UI slot.
        setAuthStage('activate');
    }, []);

    const handleForgotPassword = React.useCallback(() => {
        setAuthStage('forgot-password');
    }, []);

    const [twoFactorIdentity, setTwoFactorIdentity] = React.useState<string | undefined>(undefined);
    const handleTwoFactor = React.useCallback((identity: string) => {
        setTwoFactorIdentity(identity);
        setAuthStage('two-factor');
    }, []);

    const handleAuthComplete = React.useCallback(() => {
        setAuthStage('done');
        onAuthComplete?.();
    }, [onAuthComplete]);

    return {
        loading,
        sent,
        accountExists,
        submittedEmail,
        loginIdentity,
        submit,
        sendReceiptOnly,
        authStage,
        setAuthStage,
        openInlineAuth,
        closeInlineAuth,
        handleSignupSuccess,
        handleAuthComplete,
        handleForgotPassword,
        handleTwoFactor,
        twoFactorIdentity,
        rememberCurrentUrl,
        isPhoneAuth
    };
}
